import React, { useState, useEffect } from 'react';

const API = '';

export default function NotificationSenderPage({ user }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sendTo, setSendTo] = useState('all');
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [counts, setCounts] = useState({});

  const headers = { 'Authorization': 'Bearer ' + localStorage.getItem('authToken'), 'Content-Type': 'application/json' };

  useEffect(() => { loadUsers(); loadCounts(); }, []);

  const loadUsers = async () => {
    try {
      const res = await fetch(API + '/api/users', { headers });
      const data = await res.json();
      setUsers(data.users || []);
    } catch (e) { console.error(e); }
  };

  const loadCounts = async () => {
    try {
      const res = await fetch(API + '/api/notifications/audience-counts', { headers });
      const data = await res.json();
      if (data.success) setCounts(data.counts || {});
    } catch (e) { console.error(e); }
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) { alert('Title and message are required'); return; }
    if (sendTo === 'selected' && selectedUsers.length === 0) { alert('Please select at least one user'); return; }
    
    setSending(true); setResult(null);
    try {
      const payload = { title: title.trim(), body: body.trim() };
      if (sendTo === 'selected') payload.user_ids = selectedUsers;
      else if (sendTo !== 'all') payload.segment = sendTo;
      
      const res = await fetch(API + '/api/notifications/send', { method: 'POST', headers, body: JSON.stringify(payload) });
      const data = await res.json();
      
      if (data.success) {
        setResult({ type: 'success', message: `Notification sent to ${data.sent} user${data.sent !== 1 ? 's' : ''}` });
        setHistory(prev => [{ title: title.trim(), body: body.trim(), sent: data.sent, time: new Date().toLocaleString(), target: sendTo === 'all' ? 'All users' : selectedUsers.length + ' selected' }, ...prev]);
        setTitle(''); setBody('');
      } else {
        setResult({ type: 'error', message: data.error || 'Failed to send' });
      }
    } catch (e) {
      setResult({ type: 'error', message: 'Connection error' });
    }
    setSending(false);
  };

  const toggleUser = (id) => {
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(u => u !== id) : [...prev, id]);
  };

  const downloadSelectedCsv = () => {
    const selected = users.filter(u => selectedUsers.includes(u.id));
    if (selected.length === 0) return;
    const headers = ['ID', 'First Name', 'Last Name', 'Email', 'Phone', 'Has Push Token', 'Created At'];
    const escape = v => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"';
    const csv = [headers, ...selected.map(u => [u.id, u.first_name, u.last_name, u.email, u.phone, u.push_token ? 'Yes' : 'No', u.created_at])]
      .map(row => row.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `lmp-selected-users-${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const filteredUsers = users.filter(u => 
    (u.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.first_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.last_name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">Push Notifications</h2>

      <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
        <h3 className="font-semibold text-lg">Compose Notification</h3>
        <div className="flex gap-2 flex-wrap mb-2">
          <span className="text-xs text-gray-400 py-1">Templates:</span>
          <button onClick={() => { setTitle('New Charger Available ⚡'); setBody('A new LMPEnergy charger is now live near you! Open the app to find it.'); }} className="px-2 py-1 bg-gray-100 rounded text-xs hover:bg-gray-200">New Charger</button>
          <button onClick={() => { setTitle('Maintenance Notice 🔧'); setBody('Scheduled maintenance on [charger name] on [date]. The charger will be temporarily unavailable.'); }} className="px-2 py-1 bg-gray-100 rounded text-xs hover:bg-gray-200">Maintenance</button>
          <button onClick={() => { setTitle('Special Rate 💰'); setBody('Enjoy reduced charging rates this weekend! All chargers at £0.25/kWh from Friday to Sunday.'); }} className="px-2 py-1 bg-gray-100 rounded text-xs hover:bg-gray-200">Special Rate</button>
          <button onClick={() => { setTitle('App Update Available 📱'); setBody('A new version of the LMPEnergy app is available. Update now for the latest features and improvements.'); }} className="px-2 py-1 bg-gray-100 rounded text-xs hover:bg-gray-200">App Update</button>
          <button onClick={() => { setTitle('Welcome to LMPEnergy! 👋'); setBody('Thanks for joining. Find your nearest charger, add a payment method, and start charging today!'); }} className="px-2 py-1 bg-gray-100 rounded text-xs hover:bg-gray-200">Welcome</button>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. New charger available!" className="w-full px-3 py-2 border rounded-lg text-sm" maxLength={60} />
          <p className="text-xs text-gray-400 mt-1">{title.length}/60</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">Message</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="e.g. A new 22kW charger is now live on High Street..." className="w-full px-3 py-2 border rounded-lg text-sm h-24 resize-none" maxLength={200} />
          <p className="text-xs text-gray-400 mt-1">{body.length}/200</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">Send to</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {[
              { key: 'all', label: 'All Users', sub: 'Everyone with notifications', count: counts.all },
              { key: 'new_7', label: 'New (7 days)', sub: 'For welcome messages', count: counts.new_7 },
              { key: 'new_30', label: 'New (30 days)', sub: 'Joined recently', count: counts.new_30 },
              { key: 'no_sessions', label: 'No Sessions Yet', sub: 'Nudge first charge', count: counts.no_sessions },
              { key: 'inactive_60', label: 'Inactive 60+ days', sub: 'Re-engagement', count: counts.inactive_60 },
              { key: 'home_charger', label: 'Home Charger Users', sub: 'Have home charger', count: counts.home_charger },
              { key: 'selected', label: 'Selected Users', sub: 'Pick specific users', count: null },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => setSendTo(opt.key)}
                className={`px-3 py-2 rounded-lg text-sm text-left transition ${sendTo === opt.key ? 'bg-green-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'}`}
              >
                <div className="font-semibold">{opt.label}</div>
                <div className={`text-xs ${sendTo === opt.key ? 'text-green-100' : 'text-gray-500'}`}>
                  {opt.count != null ? `${opt.count} recipient${opt.count !== 1 ? 's' : ''}` : opt.sub}
                </div>
              </button>
            ))}
          </div>
        </div>

        {sendTo === 'selected' && (
          <div className="space-y-3">
            {selectedUsers.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <span className="text-sm font-medium text-green-700">
                    {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={downloadSelectedCsv}
                      className="text-xs px-3 py-1 bg-white border border-green-300 text-green-700 rounded hover:bg-green-100 font-medium"
                      title="Download selected users as CSV"
                    >
                      ⬇ Download CSV
                    </button>
                    <button
                      onClick={() => setSelectedUsers([])}
                      className="text-xs px-3 py-1 bg-white border border-gray-300 text-gray-600 rounded hover:bg-gray-100"
                      title="Clear all"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {selectedUsers.map(id => {
                    const u = users.find(x => x.id === id);
                    if (!u) return null;
                    return (
                      <span key={id} className="inline-flex items-center bg-white border border-green-300 rounded-full pl-2.5 pr-1 py-0.5 text-xs">
                        <span className="font-medium text-gray-800">{u.first_name} {u.last_name}</span>
                        {!u.push_token && <span className="ml-1.5 text-[10px] text-gray-400">(no token)</span>}
                        <button
                          onClick={() => toggleUser(id)}
                          className="ml-1.5 w-5 h-5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 flex items-center justify-center"
                          title="Remove"
                        >×</button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name or email..." className="w-full px-3 py-2 border rounded-lg text-sm" />
            <div className="max-h-48 overflow-y-auto border rounded-lg">
              {filteredUsers.slice(0, 50).map(u => (
                <label key={u.id} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer border-b text-sm last:border-b-0">
                  <input type="checkbox" checked={selectedUsers.includes(u.id)} onChange={() => toggleUser(u.id)} className="mr-3" />
                  <span className="font-medium">{u.first_name} {u.last_name}</span>
                  <span className="ml-2 text-gray-400">{u.email}</span>
                  {u.push_token && <span className="ml-auto text-xs text-green-600">📱</span>}
                  {!u.push_token && <span className="ml-auto text-xs text-gray-300">No token</span>}
                </label>
              ))}
              {filteredUsers.length === 0 && (
                <div className="px-3 py-4 text-center text-sm text-gray-400">No users match your search</div>
              )}
            </div>
          </div>
        )}

        {result && (
          <div className={`p-3 rounded-lg text-sm ${result.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {result.message}
          </div>
        )}

        <button onClick={handleSend} disabled={sending} className="px-6 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
          {sending ? 'Sending...' : 'Send Notification'}
        </button>
      </div>

      {history.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-lg mb-4">Sent This Session</h3>
          <table className="w-full text-sm">
            <thead><tr className="border-b"><th className="text-left py-2 text-gray-500">Time</th><th className="text-left py-2 text-gray-500">Title</th><th className="text-left py-2 text-gray-500">Message</th><th className="text-left py-2 text-gray-500">Target</th><th className="text-left py-2 text-gray-500">Sent</th></tr></thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i} className="border-b">
                  <td className="py-2 text-gray-400">{h.time}</td>
                  <td className="py-2 font-medium">{h.title}</td>
                  <td className="py-2 text-gray-600">{h.body.substring(0, 50)}...</td>
                  <td className="py-2">{h.target}</td>
                  <td className="py-2 text-green-600">{h.sent}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
